// enhance.ts — Fallback / enhance effect generation
// Port of AudioDirector.enhanceProfileWithFallbackEffects (and its helpers)
// from Mr Roast/Services/Audio/AudioDirector.swift (~lines 190-424).

import type { AudioProfile, SoundEffectCategory, SoundEffectOpportunity } from './types';
import { makeRng, pick } from './rng';

// ---------------------------------------------------------------------------
// MARK: - Common Trigger Word Table
// Verbatim from AudioDirector.swift generateFallbackEffects (~lines 249-270)
// ---------------------------------------------------------------------------

export const COMMON_TRIGGERS: ReadonlyArray<{
  words: readonly string[];
  category: SoundEffectCategory;
  confidence: number;
}> = [
  { words: ['look', 'looks'],     category: 'reactions', confidence: 0.4 },
  { words: ['like', 'seems'],     category: 'comedy',    confidence: 0.3 },
  { words: ['face', 'head'],      category: 'reactions', confidence: 0.5 },
  { words: ['hair', 'style'],     category: 'comedy',    confidence: 0.4 },
  { words: ['clothes', 'outfit'], category: 'comedy',    confidence: 0.4 },
  { words: ['what', 'why'],       category: 'reactions', confidence: 0.3 },
  { words: ['that', 'this'],      category: 'comedy',    confidence: 0.3 },
  { words: ['big', 'small'],      category: 'comedy',    confidence: 0.4 },
  { words: ['old', 'young'],      category: 'comedy',    confidence: 0.4 },
  { words: ['bad', 'good'],       category: 'reactions', confidence: 0.3 },
  { words: ['ugly', 'pretty'],    category: 'reactions', confidence: 0.5 },
  { words: ['weird', 'strange'],  category: 'comedy',    confidence: 0.4 },
  { words: ['shit', 'fuck'],      category: 'reactions', confidence: 0.5 },
  { words: ['damn', 'hell'],      category: 'reactions', confidence: 0.4 },
  { words: ['car', 'drive'],      category: 'objects',   confidence: 0.5 },
  { words: ['food', 'eat'],       category: 'objects',   confidence: 0.4 },
  { words: ['job', 'work'],       category: 'comedy',    confidence: 0.3 },
  { words: ['money', 'broke'],    category: 'comedy',    confidence: 0.4 },
  { words: ['house', 'home'],     category: 'objects',   confidence: 0.3 },
  { words: ['phone', 'call'],     category: 'objects',   confidence: 0.4 },
] as const;

// ---------------------------------------------------------------------------
// MARK: - Sentence-end category arrays
// Verbatim from AudioDirector.swift generateSentenceEndEffects (~lines 328-330)
// ---------------------------------------------------------------------------

const EXCLAMATION_EFFECTS: SoundEffectCategory[] = ['reactions', 'comedy'];
const QUESTION_EFFECTS: SoundEffectCategory[]    = ['reactions', 'transitions'];
const STATEMENT_EFFECTS: SoundEffectCategory[]   = ['comedy', 'transitions', 'objects'];

// ---------------------------------------------------------------------------
// MARK: - Rhythm category rotation
// Verbatim from AudioDirector.swift generateRhythmEffects (~lines 399-400)
// ---------------------------------------------------------------------------

const RHYTHM_CATEGORIES: SoundEffectCategory[] = ['transitions', 'comedy', 'objects'];
const RHYTHM_INTERVAL = 10;

// ---------------------------------------------------------------------------
// MARK: - Internal helpers
// ---------------------------------------------------------------------------

/** Split script on whitespace; filter empty tokens. */
function splitWords(text: string): string[] {
  return text.split(/\s+/).filter(w => w.length > 0);
}

/**
 * Strip leading and trailing non-word characters (mirrors Swift's
 * trimmingCharacters(in: .punctuationCharacters)).
 */
function trimPunctuation(word: string): string {
  return word.replace(/^[^\w]+|[^\w]+$/g, '');
}

// ---------------------------------------------------------------------------
// MARK: - targetCount (exported for tests)
// Swift: min(max(6, wordCount / 18), 12)  (integer division)
// ---------------------------------------------------------------------------

/**
 * Dynamic target effect count based on script length.
 * Mirrors Swift: `min(max(6, wordCount / 18), 12)`
 */
export function targetCount(script: string): number {
  const wordCount = splitWords(script).length;
  return Math.min(Math.max(6, Math.floor(wordCount / 18)), 12);
}

// ---------------------------------------------------------------------------
// MARK: - generateSentenceEndEffects
// Port of AudioDirector.generateSentenceEndEffects (~lines 319-382)
// ---------------------------------------------------------------------------

function generateSentenceEndEffects(
  script: string,
  existingEffects: SoundEffectOpportunity[],
  rng: () => number,
): SoundEffectOpportunity[] {
  const sentenceEffects: SoundEffectOpportunity[] = [];
  const existingPhrases = new Set(existingEffects.map(e => e.triggerPhrase.toLowerCase()));

  // Swift: let words = script.components(separatedBy: .whitespacesAndNewlines).filter { !$0.isEmpty }
  const words = splitWords(script);

  for (let index = 0; index < words.length; index++) {
    const word = words[index];

    // Check if word ends with sentence punctuation
    const lastChar = word.slice(-1);
    if (!'.!?'.includes(lastChar)) continue;

    // Get the last meaningful word (strip punctuation) — mirror Swift cleanWord
    const cleanWord = trimPunctuation(word).toLowerCase();
    if (!cleanWord) continue;

    // Skip if we already have an effect for this word
    if (existingPhrases.has(cleanWord)) continue;

    // Skip very short sentences (less than 4 words) — Swift: if index < 3 { continue }
    if (index < 3) continue;

    // Determine effect category based on punctuation
    let category: SoundEffectCategory;
    let confidence: number;

    switch (lastChar) {
      case '!':
        // Exclamations get higher confidence and reactions/comedy
        category   = pick(rng, EXCLAMATION_EFFECTS);
        confidence = 0.6;
        break;
      case '?':
        // Questions get medium confidence
        category   = pick(rng, QUESTION_EFFECTS);
        confidence = 0.5;
        break;
      default:
        // Statements get lower confidence but still worth it
        category   = pick(rng, STATEMENT_EFFECTS);
        confidence = 0.4;
        break;
    }

    sentenceEffects.push({
      triggerPhrase:     cleanWord,
      suggestedCategory: category,
      timing:            'after', // After the word for punctuation
      confidence,
    });

    // Limit sentence effects to avoid overdoing it
    if (sentenceEffects.length >= 3) break;
  }

  return sentenceEffects;
}

// ---------------------------------------------------------------------------
// MARK: - generateFallbackEffects (common trigger words)
// Port of AudioDirector.generateFallbackEffects (~lines 248-312)
// ---------------------------------------------------------------------------

function generateFallbackEffects(
  script: string,
  existingEffects: SoundEffectOpportunity[],
): SoundEffectOpportunity[] {
  // Swift: script.lowercased().components(separatedBy: .whitespacesAndNewlines)
  // Note: Swift does NOT filter empty strings here; neither do we.
  const scriptWords = script.toLowerCase().split(/\s+/);
  const existingPhrases = new Set(existingEffects.map(e => e.triggerPhrase.toLowerCase()));

  const fallbackEffects: SoundEffectOpportunity[] = [];

  for (const { words, category, confidence } of COMMON_TRIGGERS) {
    for (const triggerWord of words) {
      if (scriptWords.includes(triggerWord)) {
        const triggerPhrase = triggerWord;

        // Skip if we already have this phrase
        if (existingPhrases.has(triggerPhrase)) {
          continue;
        }

        // Create fallback effect
        fallbackEffects.push({
          triggerPhrase,
          suggestedCategory: category,
          timing:            'during',
          confidence,
        });

        // Stop after finding enough effects
        if (fallbackEffects.length >= 4) {
          break; // inner for
        }
      }
    }

    if (fallbackEffects.length >= 4) {
      break; // outer for
    }
  }

  return fallbackEffects;
}

// ---------------------------------------------------------------------------
// MARK: - generateRhythmEffects
// Port of AudioDirector.generateRhythmEffects (~lines 390-424)
// ---------------------------------------------------------------------------

function generateRhythmEffects(
  script: string,
  existingEffects: SoundEffectOpportunity[],
  target: number,
): SoundEffectOpportunity[] {
  const rhythmEffects: SoundEffectOpportunity[] = [];
  const existingPhrases = new Set(existingEffects.map(e => e.triggerPhrase.toLowerCase()));

  // Swift: let words = script.components(separatedBy: .whitespacesAndNewlines).filter { !$0.isEmpty }
  const words = splitWords(script);

  // Place effects at natural rhythm points (every 10 words)
  // Swift: for i in stride(from: rhythmInterval, to: words.count, by: rhythmInterval)
  for (let i = RHYTHM_INTERVAL; i < words.length; i += RHYTHM_INTERVAL) {
    // Swift: guard rhythmEffects.count + existingEffects.count < targetCount else { break }
    if (rhythmEffects.length + existingEffects.length >= target) break;

    const word = trimPunctuation(words[i]).toLowerCase();

    // Skip if already has effect or empty
    if (existingPhrases.has(word) || !word) continue;

    // Pick a varied category (rotation verbatim from Swift)
    const category = RHYTHM_CATEGORIES[rhythmEffects.length % RHYTHM_CATEGORIES.length];

    rhythmEffects.push({
      triggerPhrase:     word,
      suggestedCategory: category,
      timing:            'during',
      confidence:        0.35, // Lower confidence for rhythm-based
    });
  }

  return rhythmEffects;
}

// ---------------------------------------------------------------------------
// MARK: - Public API
// ---------------------------------------------------------------------------

/**
 * Enhance an audio profile with fallback sound effects when AI didn't generate
 * enough opportunities.  Port of AudioDirector.enhanceProfileWithFallbackEffects.
 *
 * @param profile  Original AI-generated audio profile.
 * @param script   Roast script to analyse.
 * @param rng      Seeded PRNG for deterministic category selection in sentence-end
 *                 effects (mirrors Swift's randomElement()).  Defaults to a fixed seed.
 * @returns        Final array of SoundEffectOpportunity objects.
 */
export function enhance(
  profile: AudioProfile,
  script: string,
  rng: () => number = makeRng('enhance'),
): SoundEffectOpportunity[] {
  const currentEffects = profile.soundEffectOpportunities;
  const target = targetCount(script);

  // If we already have enough effects, just add sentence punctuation
  if (currentEffects.length >= target) {
    const sentenceEffects = generateSentenceEndEffects(script, currentEffects, rng);
    // Swift returns currentEffects + sentenceEffects with NO slice in this branch
    return [...currentEffects, ...sentenceEffects];
  }

  // Generate multiple types of fallback effects
  const allFallbackEffects: SoundEffectOpportunity[] = [];

  // 1. Sentence-end punctuation effects (highest priority)
  const sentenceEffects = generateSentenceEndEffects(script, currentEffects, rng);
  allFallbackEffects.push(...sentenceEffects);

  // 2. Common trigger word effects
  const fallbackEffects = generateFallbackEffects(
    script,
    [...currentEffects, ...sentenceEffects],
  );
  allFallbackEffects.push(...fallbackEffects);

  // 3. Rhythm-based effects for natural flow
  if (allFallbackEffects.length + currentEffects.length < target) {
    const rhythmEffects = generateRhythmEffects(
      script,
      [...currentEffects, ...allFallbackEffects],
      target,
    );
    allFallbackEffects.push(...rhythmEffects);
  }

  // Combine and limit to target + 2 (allow slight overage — mirrors Swift .prefix(targetCount + 2))
  return [...currentEffects, ...allFallbackEffects].slice(0, target + 2);
}
